import {
    CSSProperties,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    calendarOutline,
    chevronForwardOutline,
    americanFootballOutline,
    schoolOutline,
    refreshOutline,
    shirtOutline,
    statsChartOutline
} from "ionicons/icons";
import { useHistory, useParams } from "react-router-dom";
import {
    Competition,
    Game,
    GameDetail,
    GamePlay,
    Player as PlayerModel,
    PlayerStatistics,
    Team,
    getCompetitions,
    getGameDetail,
    getGames,
    getPlayerDetail,
    getPlayersList,
    getTeams
} from "../services/mexfbaApi";


import "./Player.css";

interface RouteParams {
    id: string;
}

interface TeamThemeStyle extends CSSProperties {
    "--team-color": string;
    "--team-text": string;
}

export interface PlayerGame {
    game: Game;
    detail: GameDetail;
}

function getPositionName(position?: string): string {
    const positions: Record<string, string> = {
        "1": "Quarterback",
        "2": "Running Back",
        "3": "Wide Receiver",
        "4": "Defensive Line",
        "5": "Linebacker",
        "6": "Defensive Back",
        "7": "Kicker",
        "8": "Offensive Line"
    };

    return positions[position || ""] || "Jugador";
}

function getPositionShortName(position?: string): string {
    const positions: Record<string, string> = {
        "1": "QB",
        "2": "RB",
        "3": "WR",
        "4": "DL",
        "5": "LB",
        "6": "DB",
        "7": "K",
        "8": "OL"
    };

    return positions[position || ""] || "—";
}

function getTeamColor(color?: string): string {
    const validColor = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

    if (!color || !validColor.test(color.trim())) {
        return "#1c477d";
    }

    return color.trim();
}

function getContrastTextColor(color: string): string {
    let normalizedColor = color.replace("#", "");

    if (normalizedColor.length === 3) {
        normalizedColor = normalizedColor
            .split("")
            .map(function (character) {
                return character + character;
            })
            .join("");
    }

    const red = parseInt(normalizedColor.substring(0, 2), 16);
    const green = parseInt(normalizedColor.substring(2, 4), 16);
    const blue = parseInt(normalizedColor.substring(4, 6), 16);

    const brightness =
        (red * 299 + green * 587 + blue * 114) / 1000;

    return brightness > 155 ? "#132238" : "#ffffff";
}

function getNumber(value: unknown): number {
    const parsedValue = Number(value);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function hasValue(value: unknown): boolean {
    return (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
    );
}

function getPlayValue(
    play: GamePlay,
    property: string
): string {
    return String(
        (play as Record<string, unknown>)[property] || ""
    );
}

export function getPlays(detail: GameDetail): GamePlay[] {
    if (!detail.Jugadas) {
        return [];
    }

    return Array.isArray(detail.Jugadas)
        ? detail.Jugadas
        : Object.values(detail.Jugadas);
}

function createEmptyStats(): PlayerStatistics {
    return {
        passingAttempts: 0,
        passingCompletions: 0,
        passingYards: 0,
        passingTouchdowns: 0,
        interceptionsThrown: 0,

        rushingAttempts: 0,
        rushingYards: 0,
        rushingTouchdowns: 0,

        receptions: 0,
        receivingYards: 0,
        receivingTouchdowns: 0,

        soloTackles: 0,
        assistedTackles: 0,
        sacks: 0,
        assistedSacks: 0,
        interceptions: 0,
        forcedFumbles: 0,
        recoveredFumbles: 0,
        defensiveTouchdowns: 0,
        passDeflections: 0,

        fieldGoalsMade: 0,
        fieldGoalsAttempted: 0,
        extraPointsMade: 0,
        extraPointsAttempted: 0,
        longestFieldGoal: 0,

        gamesPlayed: 0
    };
}

export function playerAppearsInPlay(
    play: GamePlay,
    playerId: string
): boolean {
    const relevantFields = [
        "QB",
        "RB",
        "WR",
        "K",
        "tkl",
        "assTkl",
        "assTkll",
        "sack",
        "assSack",
        "assSackk",
        "int",
        "fumbleF",
        "fumbleR",
        "passDef"
    ];

    return relevantFields.some(function (field) {
        return getPlayValue(play, field) === playerId;
    });
}

export function calculateStats(
    games: PlayerGame[],
    player: PlayerModel
): PlayerStatistics {
    const stats = createEmptyStats();

    games.forEach(function (playerGame) {
        const plays = getPlays(playerGame.detail);

        const playedGame = plays.some(function (play) {
            return playerAppearsInPlay(play, player.id);
        });

        if (playedGame) {
            stats.gamesPlayed += 1;
        }

        plays.forEach(function (play) {
            const yards = getNumber(getPlayValue(play, "yds"));
            const touchdown = getNumber(getPlayValue(play, "td")) === 6;
            const defensiveTouchdown = getPlayValue(play, "deftd") === player.id;


            /*
                Estadísticas de quarterback.
            */
            if (getPlayValue(play, "QB") === player.id) {
                const interceptionThrown = hasValue(
                    getPlayValue(play, "int")
                );

                if (!hasValue(play.sack)) {
                    stats.passingAttempts += 1;
                }

                if (yards !== 0) {
                    stats.passingCompletions += 1;
                    stats.passingYards += yards;
                }

                if (touchdown) {
                    stats.passingTouchdowns += 1;
                }

                if (interceptionThrown) {
                    stats.interceptionsThrown += 1;
                }
            }

            /*
                Estadísticas de corredor.
            */
            if (getPlayValue(play, "RB") === player.id) {
                stats.rushingAttempts += 1;
                stats.rushingYards += yards;

                if (touchdown) {
                    stats.rushingTouchdowns += 1;
                }
            }

            /*
                Estadísticas de receptor.
            */
            if (getPlayValue(play, "WR") === player.id) {
                if (yards !== 0) {
                    stats.receptions += 1;
                    stats.receivingYards += yards;
                }

                if (touchdown) {
                    stats.receivingTouchdowns += 1;
                }
            }

            /*
                Estadísticas defensivas.
            */
            if (getPlayValue(play, "tkl") === player.id) {
                stats.soloTackles += 1;
            }

            if (
                getPlayValue(play, "assTkl") === player.id ||
                getPlayValue(play, "assTkll") === player.id
            ) {
                stats.assistedTackles += 1;
            }

            if (
                getPlayValue(play, "sack") === player.id &&
                !hasValue(getPlayValue(play, "assSack"))
            ) {
                stats.sacks += 1;
                stats.soloTackles += 1;
            }

            if (
                getPlayValue(play, "assSack") === player.id ||
                getPlayValue(play, "assSackk") === player.id
            ) {
                stats.assistedSacks += 1;
                stats.assistedTackles += 1;
            }

            if (getPlayValue(play, "int") === player.id) {
                stats.interceptions += 1;

                if (defensiveTouchdown) {
                    stats.defensiveTouchdowns += 1;
                }
            }

            if (getPlayValue(play, "fumbleF") === player.id) {
                stats.forcedFumbles += 1;
            }

            if (getPlayValue(play, "fumbleR") === player.id) {
                stats.recoveredFumbles += 1;

                if (defensiveTouchdown) {
                    stats.defensiveTouchdowns += 1;
                }
            }

            if (getPlayValue(play, "passDef") === player.id) {
                stats.passDeflections += 1;
            }

            /*
                Estadísticas de kicker.
            */
            if (getPlayValue(play, "K") === player.id) {
                const fg_xp= getPlayValue(play, "d");
                const fieldGoalValue = getPlayValue(play, "td");
                const extraPointValue = getPlayValue(play, "td");

                if (getNumber(fg_xp) > 4) {
                    stats.extraPointsAttempted += 1;

                    if (getNumber(extraPointValue) > 0) {
                        stats.extraPointsMade += 1;
                    }
                }

                else if (hasValue(fieldGoalValue)) {
                    stats.fieldGoalsAttempted += 1;

                    if (getNumber(fieldGoalValue) > 0) {
                        stats.fieldGoalsMade += 1;
                        stats.longestFieldGoal = Math.max(
                            stats.longestFieldGoal,
                            yards
                        );
                    }
                }
            }
        });
    });

    return stats;
}

export function getPassingRating(stats: PlayerStatistics): string {
    if (stats.passingAttempts === 0) {
        return "0.0";
    }

    function limit(value: number): number {
        return Math.max(0, Math.min(2.375, value));
    }

    const attempts = stats.passingAttempts;
    const a = limit(
        ((stats.passingCompletions / attempts) - 0.3) * 5
    );

    const b = limit(
        ((stats.passingYards / attempts) - 3) * 0.25
    );

    const c = limit(
        (stats.passingTouchdowns / attempts) * 20
    );

    const d = limit(
        2.375 - (stats.interceptionsThrown / attempts) * 25
    );

    return (((a + b + c + d) / 6) * 100).toFixed(1);
}

export function getAverage(
    value: number,
    attempts: number
): string {
    if (attempts === 0) {
        return "0.0";
    }

    return (value / attempts).toFixed(1);
}

function formatDate(date?: string): string {
    if (!date) {
        return "Fecha por confirmar";
    }

    const parsedDate = new Date(
        date.length === 10 ? `${date}T12:00:00` : date
    );

    if (Number.isNaN(parsedDate.getTime())) {
        return date;
    }

    return new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric"
    }).format(parsedDate);
}

interface StatItem {
    label: string;
    value: string | number;
}

interface StatsSummaryProps {
    player: PlayerModel;
    statistics: PlayerStatistics;
}

const StatsSummary: React.FC<StatsSummaryProps> = function ({
    player,
    statistics
}) {
    let items: StatItem[] = [];

    if (player.posicion === "1") {
        items = [
            {
                label: "QBR",
                value: getPassingRating(statistics)
            },
            {
                label: "YDS",
                value: statistics.passingYards
            },
            {
                label: "TD",
                value: statistics.passingTouchdowns
            },
            {
                label: "INT",
                value: statistics.interceptionsThrown
            }
        ];
    } else if (player.posicion === "2") {
        items = [
            {
                label: "ATT",
                value: statistics.rushingAttempts
            },
            {
                label: "YDS",
                value: statistics.rushingYards
            },
            {
                label: "TD",
                value: statistics.rushingTouchdowns
            },
            {
                label: "AVG",
                value: getAverage(
                    statistics.rushingYards,
                    statistics.rushingAttempts
                )
            }
        ];
    } else if (player.posicion === "3") {
        items = [
            {
                label: "REC",
                value: statistics.receptions
            },
            {
                label: "YDS",
                value: statistics.receivingYards
            },
            {
                label: "TD",
                value: statistics.receivingTouchdowns
            },
            {
                label: "AVG",
                value: getAverage(
                    statistics.receivingYards,
                    statistics.receptions
                )
            }
        ];
    } else if (
        player.posicion === "4" ||
        player.posicion === "5"
    ) {
        items = [
            {
                label: "TKL",
                value:
                    statistics.soloTackles +
                    statistics.assistedTackles
            },
            {
                label: "SACK",
                value: (
                    statistics.sacks +
                    statistics.assistedSacks / 2
                ).toFixed(1)
            },
            {
                label: "FF",
                value: statistics.forcedFumbles
            },
            {
                label: "INT",
                value: statistics.interceptions
            }
        ];
    } else if (player.posicion === "6") {
        items = [
            {
                label: "TKL",
                value:
                    statistics.soloTackles +
                    statistics.assistedTackles
            },
            {
                label: "INT",
                value: statistics.interceptions
            },
            {
                label: "PD",
                value: statistics.passDeflections
            },
            {
                label: "TD",
                value: statistics.defensiveTouchdowns
            }
        ];
    } else if (player.posicion === "7") {
        items = [
            {
                label: "FG",
                value: `${statistics.fieldGoalsMade}/${statistics.fieldGoalsAttempted}`
            },
            {
                label: "XP",
                value: `${statistics.extraPointsMade}/${statistics.extraPointsAttempted}`
            },
            {
                label: "LNG",
                value: statistics.longestFieldGoal
            },
            {
                label: "PJ",
                value: statistics.gamesPlayed
            }
        ];
    } else {
        items = [
            {
                label: "PJ",
                value: statistics.gamesPlayed
            },
            {
                label: "TD",
                value:
                    statistics.rushingTouchdowns +
                    statistics.receivingTouchdowns +
                    statistics.defensiveTouchdowns
            },
            {
                label: "YDS",
                value:
                    statistics.rushingYards +
                    statistics.receivingYards
            },
            {
                label: "POS",
                value: getPositionShortName(player.posicion)
            }
        ];
    }

    return (
        <div className="player-stats-grid">
            {items.map(function (item) {
                return (
                    <div
                        className="player-stat"
                        key={item.label}
                    >
                        <strong>{item.value}</strong>
                        <span>{item.label}</span>
                    </div>
                );
            })}
        </div>
    );
};
function getPlayerImageUrl(image?: string): string {
    if (!image) {
        return "";
    }

    if (
        image.startsWith("http://") ||
        image.startsWith("https://") ||
        image.startsWith("data:")
    ) {
        return image;
    }

    const normalizedImage = image.replace(
        /^(\.\.\/)+/,
        ""
    );

    return `${process.env.PUBLIC_URL || ""}/${normalizedImage}`;
}

const Player: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [player, setPlayer] = useState<PlayerModel | null>(
        null
    );

    const [teams, setTeams] = useState<Team[]>([]);
    const [competitions, setCompetitions] = useState<
        Competition[]
    >([]);

    const [regularStats, setRegularStats] =
        useState<PlayerStatistics>(createEmptyStats());

    const [playoffStats, setPlayoffStats] =
        useState<PlayerStatistics>(createEmptyStats());

    const [recentGames, setRecentGames] = useState<PlayerGame[]>(
        []
    );

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadPlayer = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [playersData, teamsData, gamesData, competitionsData] =
                    await Promise.all([
                        getPlayersList(forceRefresh),
                        getTeams(forceRefresh),
                        getGames(forceRefresh),
                        getCompetitions(forceRefresh)
                    ]);

                const basePlayer = playersData.find(function (
    currentPlayer
) {
    return currentPlayer.id === id;
});

if (!basePlayer) {
    throw new Error("Jugador no encontrado.");
}

/*
    jugadores.json contiene los datos generales.
    El archivo individual data/:id.json contiene la foto real
    y, en algunos casos, información más actualizada.
*/
const playerDetail = await getPlayerDetail(
    id,
    forceRefresh
);

const detailPlayer = playerDetail.Info as PlayerModel & {
    birthday?: string;
};

const selectedPlayer: PlayerModel = {
    ...basePlayer,
    ...detailPlayer,

    /*
        Algunos archivos individuales usan "birthday"
        y el componente usa "nacimiento".
    */
    nacimiento:
        detailPlayer.nacimiento ||
        detailPlayer.birthday ||
        basePlayer.nacimiento,

    /*
        Normaliza ../img/foto.jpg a /img/foto.jpg.
    */
    img:
        getPlayerImageUrl(detailPlayer.img) ||
        getPlayerImageUrl(basePlayer.img) ||
        undefined
};

setPlayer(selectedPlayer);
setTeams(teamsData);
setCompetitions(competitionsData);


                const competitionGames = gamesData.filter(function (
                    game
                ) {
                    return (
                        !selectedPlayer.comp_id ||
                        game.id_comp === selectedPlayer.comp_id
                    );
                });

                const gameDetails = await Promise.all(
                    competitionGames.map(async function (game) {
                        try {
                            const detail = await getGameDetail(
                                game.id,
                                forceRefresh
                            );

                            return {
                                game,
                                detail
                            };
                        } catch (gameError) {
                            console.warn(
                                `No fue posible abrir el partido ${game.id}.`,
                                gameError
                            );

                            return null;
                        }
                    })
                );

                const gamesWithDetail = gameDetails.filter(
                    function (
                        gameDetail
                    ): gameDetail is PlayerGame {
                        return gameDetail !== null;
                    }
                );

                const playerGames = gamesWithDetail.filter(function (
                    playerGame
                ) {
                    return getPlays(playerGame.detail).some(
                        function (play) {
                            return playerAppearsInPlay(
                                play,
                                selectedPlayer.id
                            );
                        }
                    );
                });

                const regularGames = playerGames.filter(function (
                    playerGame
                ) {
                    return (
                        playerGame.game.partido === "grupos" ||
                        !playerGame.game.partido
                    );
                });

                const playoffGames = playerGames.filter(function (
                    playerGame
                ) {
                    return (
                        playerGame.game.partido !== "grupos" &&
                        Boolean(playerGame.game.partido)
                    );
                });

                setRegularStats(
                    calculateStats(regularGames, selectedPlayer)
                );

                setPlayoffStats(
                    calculateStats(playoffGames, selectedPlayer)
                );

                const sortedGames = [...playerGames]
                    .sort(function (firstGame, secondGame) {
                        return (
                            new Date(
                                secondGame.game.fecha || 0
                            ).getTime() -
                            new Date(
                                firstGame.game.fecha || 0
                            ).getTime()
                        );
                    })
                    .slice(0, 5);

                setRecentGames(sortedGames);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar el jugador:",
                    requestError
                );

                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : "No fue posible cargar la ficha del jugador."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadPlayer();
        },
        [loadPlayer]
    );

    const playerTeam = useMemo(function () {
        if (!player?.equipo_id) {
            return undefined;
        }

        return teams.find(function (team) {
            return team.id === player.equipo_id;
        });
    }, [player, teams]);

    const playerCompetition = useMemo(function () {
        if (!player?.comp_id) {
            return undefined;
        }

        return competitions.find(function (competition) {
            return competition.id === player.comp_id;
        });
    }, [player, competitions]);

    const teamColor = getTeamColor(playerTeam?.color);

    const teamTheme: TeamThemeStyle = {
        "--team-color": teamColor,
        "--team-text": getContrastTextColor(teamColor)
    };

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadPlayer(true);
        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Jugador</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent>
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !player) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Jugador</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="player-content">
                    <div className="player-empty-state">
                        <IonIcon icon={americanFootballOutline} />

                        <strong>
                            No fue posible abrir la ficha
                        </strong>

                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadPlayer(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="player-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar"
                            onClick={function () {
                                history.goBack();
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Jugador</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar jugador"
                            disabled={refreshing}
                            onClick={function () {
                                loadPlayer(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="player-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando jugador..."
                    />
                </IonRefresher>

                <main className="player-page" style={teamTheme}>
                    <section className="player-hero">
                        <div className="player-hero__number">
                            #{player.numero || player.num || "—"}

                        </div>

                        <div className="player-hero__content">
                            <span className="player-hero__position">
                                {getPositionShortName(player.posicion)}
                            </span>

                            <h1>{player.nombre}</h1>

                            <p>
                                {getPositionName(player.posicion)}
                            </p>
                        </div>

                        {player.img ? (
    <div className="player-hero__player-photo">
        <img
            src={player.img}
            alt={`Foto de ${player.nombre}`}
        />
    </div>
) : (
    <div
        className="player-hero__player-photo player-hero__player-photo--empty"
        aria-label="Jugador sin fotografía"
    >
        <IonIcon icon={shirtOutline} />
    </div>
)}

                    </section>
<section className="player-navigation">
    <IonButton>
        Ficha
    </IonButton>

    <IonButton
        fill="outline"
        onClick={function () {
            history.push(`/statsPlayer/${player.id}`);
        }}
    >
        Estadísticas
    </IonButton>
</section>

                    <section className="player-section player-team-summary">
                        <button
                            className="player-team-link"
                            type="button"
                            disabled={!playerTeam}
                            onClick={function () {
                                if (playerTeam) {
                                    history.push(
                                        `/team/${playerTeam.id}`
                                    );
                                }
                            }}
                        >
                            {playerTeam?.logo ? (
                                <img
                                    src={playerTeam.logo}
                                    alt={`Logo de ${playerTeam.nombre}`}
                                />
                            ) : (
                                <IonIcon icon={shirtOutline} />
                            )}

                            <span>
                                <small>EQUIPO ACTUAL</small>
                                <strong>
                                    {playerTeam?.nombre ||
                                        "Equipo por confirmar"}
                                </strong>
                            </span>

                            <IonIcon
                                icon={chevronForwardOutline}
                            />
                        </button>
                    </section>

                    <section className="player-section">
                        <div className="player-section-heading">
                            <div>
                                <span>PERFIL</span>
                                <h2>Información del jugador</h2>
                            </div>
                        </div>

                        <div className="player-profile-card">
                            <div className="player-profile-item">
                                <IonIcon icon={calendarOutline} />

                                <div>
                                    <span>Fecha de nacimiento</span>
                                    <strong>
                                        {player.nacimiento ||
                                            "No disponible"}
                                    </strong>
                                </div>
                            </div>

                            <div className="player-profile-item">
    <IonIcon icon={schoolOutline} />

    <div>
        <span>Procedencia</span>
        <strong>
            {player.lm || "No disponible"}
        </strong>
    </div>
</div>

                            <div className="player-profile-item">
                                <IonIcon icon={shirtOutline} />

                                <div>
                                    <span>Posición</span>
                                    <strong>
                                        {getPositionName(
                                            player.posicion
                                        )}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </section>

                    {playerCompetition && (
                        <section className="player-section">
                            <button
                                className="player-competition-link"
                                type="button"
                                onClick={function () {
                                    history.push(
                                        `/results/${playerCompetition.id}`
                                    );
                                }}
                            >
                                {playerCompetition.logo ? (
                                    <img
                                        src={playerCompetition.logo}
                                        alt={`Logo de ${playerCompetition.nombre}`}
                                    />
                                ) : (
                                    <IonIcon icon={americanFootballOutline} />
                                )}

                                <span>
                                    <small>COMPETENCIA</small>
                                    <strong>
                                        {playerCompetition.nombre}
                                    </strong>
                                </span>

                                <IonIcon
                                    icon={chevronForwardOutline}
                                />
                            </button>
                        </section>
                    )}

                    <section className="player-section">
                        <div className="player-section-heading">
                            <div>
                                <span>
                                    {playerCompetition?.nombre ||
                                        "TEMPORADA"}
                                </span>
                                <h2>Temporada regular</h2>
                            </div>

                            <IonBadge>
                                {regularStats.gamesPlayed} PJ
                            </IonBadge>
                        </div>

                        <StatsSummary
                            player={player}
                            statistics={regularStats}
                        />
                    </section>

                    <section className="player-section">
                        <div className="player-section-heading">
                            <div>
                                <span>POSTEMPORADA</span>
                                <h2>Playoffs</h2>
                            </div>

                            <IonBadge color="warning">
                                {playoffStats.gamesPlayed} PJ
                            </IonBadge>
                        </div>

                        <StatsSummary
                            player={player}
                            statistics={playoffStats}
                        />
                    </section>

                    <section className="player-section">
                        <div className="player-section-heading">
                            <div>
                                <span>ACTIVIDAD</span>
                                <h2>Partidos recientes</h2>
                            </div>

                            <IonIcon icon={statsChartOutline} />
                        </div>

                        {recentGames.length > 0 ? (
                            <div className="player-games-list">
                                {recentGames.map(function (
                                    playerGame
                                ) {
                                    const game = playerGame.game;

                                    return (
                                        <button
                                            className="player-game-item"
                                            key={game.id}
                                            type="button"
                                            onClick={function () {
                                                history.push(
                                                    `/game/${game.id}`
                                                );
                                            }}
                                        >
                                            <div>
                                                <strong>
                                                    {game.nombre_local ||
                                                        "Local"}
                                                </strong>

                                                <span>
                                                    {game.resLocal ??
                                                        "—"}{" "}
                                                    -{" "}
                                                    {game.resVisita ??
                                                        "—"}
                                                </span>

                                                <strong>
                                                    {game.nombre_visita ||
                                                        "Visitante"}
                                                </strong>
                                            </div>

                                            <small>
                                                {formatDate(game.fecha)}
                                            </small>

                                            <IonIcon
                                                icon={
                                                    chevronForwardOutline
                                                }
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="player-no-games">
                                Aún no hay jugadas registradas para este
                                jugador en la competencia.
                            </div>
                        )}
                    </section>
                </main>
            </IonContent>
        </IonPage>
    );
};

export default Player;
